import { useCallback, useEffect, useState } from "react";
import type { PageResponse } from "@/lib/pagination";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from "@/lib/usePagination";

export type UseServerPaginationOptions<T> = {
  /** API 호출 (page: 0-based) */
  fetchPage: (page: number, size: number) => Promise<PageResponse<T>>;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  /** 검색·필터 변경 시 1페이지로 리셋 */
  resetDeps?: readonly unknown[];
  enabled?: boolean;
};

export function useServerPagination<T>({
  fetchPage,
  pageSize: initialSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  resetDeps = [],
  enabled = true,
}: UseServerPaginationOptions<T>) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialSize);
  const [items, setItems] = useState<T[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const resetKey = JSON.stringify(resetDeps);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await fetchPage(page - 1, pageSize);
      setItems(res.content ?? []);
      setTotalElements(res.totalElements ?? 0);
      setTotalPages(Math.max(1, res.totalPages ?? 1));
      if (page > Math.max(1, res.totalPages ?? 1)) {
        setPage(Math.max(1, res.totalPages ?? 1));
      }
    } catch {
      setItems([]);
      setTotalElements(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [enabled, fetchPage, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPage(1);
  };

  const safePage = Math.min(page, totalPages);
  const rangeStart = totalElements === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalElements);

  return {
    items,
    loading,
    page: safePage,
    pageSize,
    totalItems: totalElements,
    totalPages,
    rangeStart,
    rangeEnd,
    setPage,
    setPageSize,
    pageSizeOptions,
    reload: load,
  };
}
